import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '../components/PublicLayout';
import './CommonPages.css';

const Docs = () => {
  const { i18n } = useTranslation();
  const tr = i18n.language?.startsWith('tr');
  const [active, setActive] = useState('quickstart');

  const sections = useMemo(
    () =>
      tr
        ? [
            {
              id: 'quickstart',
              title: 'Hızlı başlangıç',
              body: [
                'ArcVPN hesabı oluşturun veya şirket davet kodunuzla katılın.',
                'E-posta doğrulamasını tamamlayın ve panele giriş yapın.',
                'İstemciyi indirin, oturum açın ve onaylı bir düğüme bağlanın.',
              ],
              note: 'İlk kurulum genelde birkaç dakika sürer. Takılırsanız Yardım Merkezi’ne bakın.',
            },
            {
              id: 'install',
              title: 'Kurulum',
              body: [
                'Windows / macOS / Linux: masaüstü istemciyi indirip kurun.',
                'iOS / Android: mobil uygulamayı yükleyin ve aynı hesapla giriş yapın.',
                'Kurumsal cihazlarda sessiz kurulum için IT ekibinizle paket dağıtımı kullanın.',
              ],
              note: 'Sistem gereksinimleri: güncel işletim sistemi, yönetici/izin onayı, stabil ağ.',
            },
            {
              id: 'connect',
              title: 'Bağlantı',
              body: [
                'Panelden veya istemciden kullanılabilir düğümleri görün.',
                'Bağlan’a basın; oturum durumu panoda görünür.',
                'Gerekirse split tunneling ile yalnızca kurumsal trafiği yönlendirin.',
              ],
              note: 'Bağlantı kurulmuyorsa: ağ duvarı, DNS ve 2FA oturumunu kontrol edin.',
            },
            {
              id: 'admin',
              title: 'Yönetici',
              body: [
                'Kullanıcıları davet edin, rolleri ayarlayın, şirket ayarlarını yönetin.',
                'Bağlantı geçmişini ve aktif oturumları izleyin.',
                'Lisans limitlerini ve cihaz kotasını plana göre takip edin.',
              ],
              note: 'Sistem adminleri düğüm onayı ve genel ayarlardan sorumludur.',
            },
            {
              id: 'api',
              title: 'API',
              body: [
                'Kimlik doğrulama JWT tabanlıdır: login sonrası Bearer token kullanın.',
                'Aynı origin üzerinden /api yolları nginx ile proxy edilir.',
                '2FA etkin hesaplarda doğrulama adımını tamamlamadan token dönmez.',
              ],
              code: `POST /api/login
Content-Type: application/json

{ "email": "user@company.com", "password": "••••••••" }`,
            },
            {
              id: 'troubleshoot',
              title: 'Sorun giderme',
              body: [
                'Bağlantı kopuyorsa kill switch ve ağ değişimini kontrol edin.',
                'Yavaşlık için düğüm seçimi ve split tunneling kurallarını gözden geçirin.',
                'Giriş sorunlarında e-posta doğrulama ve 2FA kod süresini kontrol edin.',
              ],
              note: 'Hâlâ çözülmediyse destek talebi açın; zaman damgası ve cihaz bilgisi ekleyin.',
            },
          ]
        : [
            {
              id: 'quickstart',
              title: 'Quick start',
              body: [
                'Create an ArcVPN account or join with a company invite code.',
                'Verify your email and sign in to the console.',
                'Install a client, sign in, and connect to an approved node.',
              ],
              note: 'Most teams are productive within a few minutes. See Help if you get stuck.',
            },
            {
              id: 'install',
              title: 'Installation',
              body: [
                'Windows / macOS / Linux: download and install the desktop client.',
                'iOS / Android: install the mobile app and use the same account.',
                'For fleet devices, distribute packages through your IT tooling.',
              ],
              note: 'Requirements: current OS, install permissions, and a stable network.',
            },
            {
              id: 'connect',
              title: 'Connecting',
              body: [
                'Browse available nodes from the client or console.',
                'Connect and confirm the session appears as active.',
                'Optionally enable split tunneling for company-only traffic.',
              ],
              note: 'If connect fails, check firewalls, DNS, and any pending 2FA step.',
            },
            {
              id: 'admin',
              title: 'Administration',
              body: [
                'Invite users, assign roles, and manage company settings.',
                'Review connection history and active sessions.',
                'Track license limits for users, devices, and connections.',
              ],
              note: 'System admins handle node approval and platform-wide settings.',
            },
            {
              id: 'api',
              title: 'API',
              body: [
                'Auth is JWT-based: use the Bearer token returned after login.',
                'In production, /api is reverse-proxied on the same origin.',
                'Accounts with 2FA must complete verification before a token is issued.',
              ],
              code: `POST /api/login
Content-Type: application/json

{ "email": "user@company.com", "password": "••••••••" }`,
            },
            {
              id: 'troubleshoot',
              title: 'Troubleshooting',
              body: [
                'Drops: check kill switch and network changes.',
                'Slowness: review node choice and split-tunnel rules.',
                'Login issues: confirm email verification and 2FA code timing.',
              ],
              note: 'Still stuck? Open a support ticket with timestamps and device details.',
            },
          ],
    [tr]
  );

  const current = sections.find((s) => s.id === active) || sections[0];

  return (
    <PublicLayout
      title={tr ? 'Dokümantasyon' : 'Documentation'}
      subtitle={tr ? 'Kurulumdan yönetime kadar pratik rehberler.' : 'Practical guides from install to administration.'}
    >
      <div className="common-page docs-shell">
        <aside className="docs-side">
          <p className="docs-side-label">{tr ? 'İçindekiler' : 'Contents'}</p>
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              className={`docs-side-link ${active === section.id ? 'is-active' : ''}`}
              onClick={() => setActive(section.id)}
            >
              {section.title}
            </button>
          ))}
          <Link to="/help" className="docs-side-help">{tr ? 'Yardım Merkezi →' : 'Help Center →'}</Link>
        </aside>

        <article className="docs-article arc-panel">
          <h2>{current.title}</h2>
          <ol className="docs-steps">
            {current.body.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ol>
          {current.code && <pre className="docs-code">{current.code}</pre>}
          {current.note && <p className="docs-note">{current.note}</p>}
          <div className="docs-links">
            <Link to="/pricing">{tr ? 'Planlara bak' : 'View plans'}</Link>
            <Link to="/contact">{tr ? 'Satış ile konuş' : 'Talk to sales'}</Link>
          </div>
        </article>
      </div>
    </PublicLayout>
  );
};

export default Docs;
